import React, {
  type Dispatch,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useResizeObserver } from '@saviynt/common';
import { Icon, Loader } from '@saviynt/design-system';
import classNames from 'classnames';

import {
  type MenuId,
  type MenuIdsResponse,
  RequestStatus,
} from '../../../models';
import {
  findAccessibleMenuLinks,
  type MenuLink,
  type SideMenuNode,
  useCookies,
  useInvertedLocalize,
} from '../../../utilities';
import { actions, thunks, useDispatch, useSelector } from '../store';

import { invertedMessages } from './messages';
import PersonalizationButton from './PersonalizationButton';
import Placeholder from './Placeholder';
import ShortcutTile from './ShortcutTile';

import './ShortcutsWidget.css';

interface ReduxState {
  personalShortcutIds:
    | MenuId[]
    | 'error'
    | 'use-defaults'
    | 'never-set'
    /** not loaded */
    | undefined;
  personalShortcutsStatus: RequestStatus;
  sideMenu: SideMenuNode[] | null;
  menuList: MenuLink[] | undefined;
  defaultShortcutIds: MenuId[] | 'unauthorized' | undefined;
  isPersonalizationEnabled: boolean;
}

type Page = 'beginning' | 'end';
type FormFactor = 'Desktop' | 'LargeTablet' | 'SmallTablet';

export interface ShortcutsWidgetProps {
  onCustomClick(id: string): void;
  className?: string;
}

const MIN_LARGE_TABLET_TILE_WIDTH = 178;
const TILE_GAP = 0.875 /* rem */ * 16; /* px */
const DEFAULT_PAGE_SIZE = 3;

/**
 * Component that reads shortcut (aka quicklink) configuration from
 * environmental redux store and renders a tile for each.
 */
export default function ShortcutsWidget({
  onCustomClick: handleCustomClick,
  ...props
}: ShortcutsWidgetProps): JSX.Element | null {
  const l = useInvertedLocalize(invertedMessages);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [page, setPage] = useState<Page>('beginning');
  const [formFactor, setFormFactor] = useState<FormFactor>('Desktop');
  const [{ userKey }] = useCookies('userKey');
  const dispatch = useDispatch();
  const {
    personalShortcutIds,
    personalShortcutsStatus,
    sideMenu,
    menuList,
    defaultShortcutIds,
    isPersonalizationEnabled,
  } = useReduxState();
  const candidateShortcutIds = (
    (['use-defaults', 'never-set'] as (typeof personalShortcutIds)[]).includes(
      personalShortcutIds
    )
      ? defaultShortcutIds
      : personalShortcutIds ?? defaultShortcutIds
  ) as MenuIdsResponse;
  const effectiveShortcuts = useMemo(
    () =>
      findAccessibleMenuLinks({
        userKey,
        sideMenu,
        menuList,
        menuIds: candidateShortcutIds,
      }),
    [candidateShortcutIds, sideMenu, menuList, userKey]
  );

  useEffect(() => {
    if (personalShortcutIds !== undefined) {
      return;
    }

    if (isPersonalizationEnabled) {
      dispatch(thunks.requestPersonalShortcuts());

      return;
    }

    dispatch(actions.personalShortcutsReceived({ shortcutIds: null }));
  }, [dispatch, isPersonalizationEnabled, personalShortcutIds]);

  const hasError =
    personalShortcutIds === 'error' && !Array.isArray(defaultShortcutIds);
  const isLoading =
    (personalShortcutIds === undefined &&
      personalShortcutsStatus === RequestStatus.Pending) ||
    defaultShortcutIds === undefined ||
    effectiveShortcuts === null;
  const ref = useResizeObserver(
    hasError || isLoading ? () => {} : handleResize
  );
  const baseClassName = `Nav_ShortcutsWidget Nav_ShortcutsWidget--is${formFactor}`;

  if (hasError) {
    const errorClassName = classNames(
      baseClassName,
      props.className,
      'Nav_ShortcutsWidget--error'
    );

    return (
      <div className={errorClassName}>
        <Icon
          kind='AlertCriticalOutline'
          color='critical-700'
          className='Nav_ShortcutsWidget-errorIcon'
        />{' '}
        <span className='Nav_ShortcutsWidget-errorMessage'>
          {l(
            'There was a problem loading your shortcuts. Refresh the page to retry.'
          )}
        </span>
      </div>
    );
  }

  if (isLoading) {
    const loadingClassName = classNames(baseClassName, props.className);

    return (
      <div className={loadingClassName}>
        <div className='Nav_ShortcutsWidget-loading'>
          <Loader kind='dots' format='inline' color='brand' size='small' />
        </div>
      </div>
    );
  }

  // For small tablet, we consider the personalization button to be a tile,
  // which means the "end" page will skip one element to make room for the
  // button.
  const skip =
    formFactor === 'SmallTablet' && page === 'end' && isPersonalizationEnabled
      ? 1
      : 0;

  const effectivePageSize = pageSize ?? effectiveShortcuts.length;
  const start =
    skip +
    (page === 'beginning' ? 0 : effectiveShortcuts.length - effectivePageSize);
  const end = skip + start + effectivePageSize;
  const handlePageIndicatorInteraction = createPageIndicatorInteractionHandler(
    page,
    setPage
  );
  const ariaLabel = page === 'beginning' ? l('First page') : l('Last page');
  const showPageIndicator = pageSize !== null;
  const isSmallTabletFormFactor = formFactor === 'SmallTablet';
  const isPersonalizationInTile = formFactor !== 'Desktop';
  const showPersonalization =
    isPersonalizationEnabled && (pageSize === null || page === 'end');
  const className = classNames(baseClassName, props.className);

  return (
    <div className={className} ref={ref}>
      <div className='Nav_ShortcutsWidget-tiles'>
        {effectiveShortcuts.length === 0 ? (
          <Placeholder
            showPromo={
              showPersonalization && personalShortcutIds === 'never-set'
            }
          />
        ) : (
          effectiveShortcuts
            .slice(start, end)
            .map((shortcut) => (
              <ShortcutTile
                className='Nav_ShortcutsWidget-tiles-tile'
                key={shortcut.id}
                shortcut={shortcut}
                onCustomClick={handleCustomClick}
                isSmallTabletFormFactor={isSmallTabletFormFactor}
              />
            ))
        )}

        {showPersonalization && (
          <div className='Nav_ShortcutsWidget-tiles-tile Nav_ShortcutsWidget-tiles-tile--isPersonalization'>
            <PersonalizationButton
              isSmallTabletFormFactor={isSmallTabletFormFactor}
              isInTile={isPersonalizationInTile}
            />
          </div>
        )}
      </div>

      {showPageIndicator && (
        <div
          className={`Nav_ShortcutsWidget-pageIndicator Nav_ShortcutsWidget-pageIndicator--${page}`}
          onClick={handlePageIndicatorInteraction}
          role='button'
          onKeyDown={handlePageIndicatorInteraction}
          tabIndex={0}
          aria-label={ariaLabel}
        />
      )}
    </div>
  );

  /** Updates responsive state (formFactor and pageSize) */
  function handleResize(contentRect: DOMRect) {
    if (ref.current === null || effectiveShortcuts === null) {
      return;
    }

    const style = window.getComputedStyle(ref.current);

    // These custom properties are set by parent component via CSS container
    // query. Note: can simplify this significantly once Firefox supports
    // container style() queries. (See
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1795622.)
    if (style.getPropertyValue('--is-desktop') === 'true') {
      setFormFactor('Desktop');
      setPageSize(null);

      return;
    }

    const personalizationTileCount = isPersonalizationEnabled ? 1 : 0;
    const effectiveItemCount =
      effectiveShortcuts.length + personalizationTileCount;
    const isPagingNeeded = effectiveItemCount > DEFAULT_PAGE_SIZE;

    if (style.getPropertyValue('--is-large-tablet') === 'true') {
      setFormFactor('LargeTablet');

      if (!isPagingNeeded) {
        setPageSize(null);

        return;
      }

      let largeTabletPageSize = DEFAULT_PAGE_SIZE;
      const tileWidth =
        (contentRect.width - TILE_GAP * DEFAULT_PAGE_SIZE) / DEFAULT_PAGE_SIZE;

      if (tileWidth < MIN_LARGE_TABLET_TILE_WIDTH) {
        largeTabletPageSize = DEFAULT_PAGE_SIZE - 1;
      }

      setPageSize(largeTabletPageSize);

      return;
    }

    setFormFactor('SmallTablet');
    setPageSize(isPagingNeeded ? DEFAULT_PAGE_SIZE : null);
  }
}

function useReduxState(): ReduxState {
  const personalShortcuts = useSelector((s) => s.shortcuts.personalShortcuts);
  const personalShortcutsStatus = useSelector(
    (s) => s.shortcuts.personalShortcutsStatus
  );

  const sideMenu = useSelector((s) => s.app.sideMenu);
  const menuList = useSelector((s) => s.profile.menuList);
  const defaultShortcutIds = useSelector((s) => s.user.homepage.quicklinks);
  const isPersonalizationEnabled = useSelector(
    (s) => s.user.domainInfo?.FEATURE_SHORTCUTS_PERSONALIZATION ?? false
  );

  let personalShortcutIds: ReduxState['personalShortcutIds'];

  if (personalShortcuts === undefined) {
    personalShortcutIds = undefined;
  } else if (personalShortcuts === 'error') {
    personalShortcutIds = 'error';
  } else if (personalShortcuts.shortcutIds === null) {
    personalShortcutIds = 'use-defaults';
  } else if (personalShortcuts.shortcutIds === undefined) {
    personalShortcutIds = 'never-set';
  } else {
    personalShortcutIds = personalShortcuts.shortcutIds;
  }

  return {
    personalShortcutIds,
    personalShortcutsStatus,
    sideMenu,
    menuList,
    defaultShortcutIds,
    isPersonalizationEnabled,
  };
}

type HtmlUIEvent = MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>;

function createPageIndicatorInteractionHandler(
  page: Page,
  setPage: Dispatch<SetStateAction<Page>>
): (e: HtmlUIEvent) => void {
  return (e: HtmlUIEvent) => {
    e.preventDefault();

    if ('key' in e && e.key !== 'Enter') {
      return;
    }

    setPage(page === 'beginning' ? 'end' : 'beginning');
  };
}
